<template>
  <main class="return-request-show">
    <img
      v-if="loading"
      :src="loadingImageUrl"
      alt="loading"
      class="loading-indicator"
    />
    <div
      v-else
      class="container"
    >
      <div
        v-if="additionalEvidenceRequired"
        class="row"
      >
        <div class="col-md-12">
          <h4>
            Additional Information Required
          </h4>
          <p class="lead">
            We still need some information in order to process
            your reimbursement/replacement request.
          </p>

          <div
            v-for="requestItem in itemsNeedEvidence"
            :key="requestItem.id"
            class="damage-claims"
          >
            <damage-claims-form
              :create-path="createDamageClaimsPath"
              :request-item-id="requestItem.id"
              :name="requestItem.name"
              :return-reason="requestItem.return_reason"
              :description-required="requestItem.description_required"
              :image-required="requestItem.image_required"
            />
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <h4 class="return-request-status">
            Return Status: {{ requestState }}
          </h4>

          <p
            v-if="hasDefectiveItem"
            class="lead defective-excerpt"
          >
            For any defective items, we will forward you an
            email from UPS with an electronic return label.
            You can then use this return label to ship us
            the defective item(s). In the meantime, we will
            send you a replacement order as soon as we
            process your request.
          </p>
          <p
            v-else
            class="lead"
          >
            You will be responsible for shipping the items
            back to us. If you would like us to provide you
            with a return label, just send us an email at
            <a href="mailto:support@candlescience.com">support@candlescience.com</a>
            and we will forward one to you. We will then subtract
            the return shipping cost from your refund amount.
          </p>

          <div
            v-if="showReturnInstructions"
            class="return-instructions"
          >
            <h5 class="return-instructions-title">
              Instructions:
            </h5>
            <p class="lead">
              Please follow these steps to return your order:
            </p>

            <ol class="return-steps">
              <template v-if="itemsMustBeReturned.length > 0">
                <li>
                  Complete and print the
                  <a :href="returnFormLink">return form</a>
                </li>
                <li>
                  Send the return to CandleScience
                  with the following items:

                  <ul class="items-to-return">
                    <li
                      v-for="item in itemsMustBeReturned"
                      :key="item.id"
                    >
                      {{ item.name }}
                    </li>
                  </ul>
                </li>
              </template>
              <template v-if="itemsCanBeKept.length > 0">
                <li>
                  The following items do not need to be returned:
                  <ul class="items-can-be-kept">
                    <li
                      v-for="item in itemsCanBeKept"
                      :key="item.id"
                    >
                      {{ item.name }}
                    </li>
                  </ul>
                  Please use what you can, and recycle
                  or safely dispose of the rest.
                </li>
              </template>
            </ol>

            <div class="button-container">
              <a
                :href="returnFormLink"
                class="btn btn-primary return-form-button"
                target="_blank"
              >
                View Return Form
              </a>
            </div>

            <template v-if="requestApproved">
              <p
                v-if="notesToCustomer"
                class="lead"
              >
                {{ notesToCustomer }}
              </p>
              <p
                v-else
                class="lead"
              >
                Your request was received and approved.
              </p>
            </template>

            <template v-if="requestDeclined">
              <p
                v-if="notesToCustomer"
                class="lead"
              >
                Your reimbursement was declined for the following reason:
                {{ notesToCustomer }}
              </p>
              <p
                v-else
                class="lead"
              >
                Sorry, but your reimbursement request was declined.
              </p>
            </template>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <h4 class="original-order">
            Request to return order {{ originalOrderNumber }}
          </h4>

          <div class="table-responsive">
            <table class="table table-striped">
              <thead>
                <tr>
                  <th class="request-item-name">Name of Item</th>
                  <th class="request-item-quantity-purchased">Quantity Purchased</th>
                  <th class="request-item-quantity">Quantity to Return</th>
                  <th class="request-item-reason">Reason for Return</th>
                  <th class="request-item-requires-return-shipment">Must be Shipped Back</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in requestItems"
                  :key="item.id"
                  :class="`request-item-${item.id}`"
                >
                  <td class="request-item-name">
                    {{ item.name }}
                  </td>
                  <td class="request-item-quantity-purchased">
                    {{ item.quantity_purchased }}
                  </td>
                  <td class="request-item-quantity">
                    {{ item.quantity_requested }}
                  </td>
                  <td class="request-item-return-reason">
                    {{ item.return_reason }}
                  </td>
                  <td class="request-item-requires-return-shipment">
                    {{ item.requires_return_shipment ? 'Yes' : 'No' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
  import axios from "axios"
  import alerts from "mixins/storefront/cart-alerts"
  import DamageClaimsForm from "./damage-claims-form";

  export default {
    components: {
      DamageClaimsForm
    },
    mixins: [alerts],
    props: {
      loadingImageUrl: {
        type: String,
        required: true,
      },
      reimbursementRequestPath: {
        type: String,
        required: true,
      },
      createDamageClaimsPath: {
        type: String,
        required: true,
      },
    },
    data() {
      return {
        loading: true,
        reimbursementRequestNumber: null,
        requestState: null,
        additionalEvidenceRequired: false,
        notesToCustomer: null,
        originalOrderNumber: null,
        requestItems: [],
        returnFormLink: null,
      };
    },
    computed: {
      hasDefectiveItem() {
        return this.requestItems.some((requestItem) => {
          return requestItem.return_reason === "Defective";
        });
      },
      itemsMustBeReturned() {
        return this.requestItems.filter((requestItem) => {
          return requestItem.requires_return_shipment;
        });
      },
      itemsNeedEvidence() {
        return this.requestItems.filter((requestItem) => {
          return requestItem.evidence_needed;
        });
      },
      itemsCanBeKept() {
        return this.requestItems.filter((requestItem) => {
          return !requestItem.requires_return_shipment;
        });
      },
      requestApproved() {
        return this.requestState === "approved";
      },
      requestDeclined() {
        return this.requestState === "declined";
      },
      showReturnInstructions() {
        return this.requestState === "pending" || this.hasDefectiveItem;
      },
    },
    created() {
      axios
        .get(this.reimbursementRequestPath)
        .then((response) => {
          switch (response.status) {
            case 200:
              this.reimbursementRequestNumber = response.data.number;
              this.requestState = response.data.state;
              this.additionalEvidenceRequired = response.data.additional_evidence_required;
              this.notesToCustomer = response.data.notesToCustomer;
              this.originalOrderNumber = response.data.original_order;
              this.requestItems = response.data.request_items;
              this.returnFormLink = response.data.return_form_link;
              break;
            case 422:
              response.data.errors.forEach((error) => {
                this.alert(error, 'danger');
              });
              break;
            default:
              break;
          }
        }).catch((error) => {
          this.alert(error, "danger");
        }).finally(() => {
          this.loading = false;
        });
    },
  };
</script>
